import { Form, FormikProvider, useFormik } from 'formik';
import { filter, sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  FormControls,
  FormikNumericField,
  usePostSavePrompt,
  useUnsavedChangesGuard,
} from 'components';
import { useCurrencies, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { validationUtils } from 'utils';

import { ShopPayoutGroups } from '../../ShopPayoutGroups';
import { ShopPayoutTradeMethods } from '../../ShopPayoutTradeMethods';
import {
  useManageShop,
  ManageShopAccordion,
  ManageShopTabActions,
} from '../components';
import { useShopSubmit } from '../hooks';

type PayoutValues = {
  payoutTradeMethodsConfigs: any[];
  payoutOrderMinAmount: number;
  payoutOrderMaxAmount: number;
  payoutMaxTimeout: number | null;
  payoutCreateOrderResponseTimeout: number | null;
};

export const PayoutTab: React.FC = () => {
  const { shopId, shopData, isNew } = useManageShop();
  const { onSavedSuccess } = usePostSavePrompt();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();
  const { isAdminOrTechOperator } = useUser();
  const { getFiatCurrencyCode } = useCurrencies();

  const { handleSubmit: submitShop } = useShopSubmit({
    shopId,
    onSuccess: onSavedSuccess,
  });

  const shopFiatCurrencyCode = useMemo(
    () => getFiatCurrencyCode(shopData?.fiatCurrencyId || ''),
    [shopData?.fiatCurrencyId, getFiatCurrencyCode],
  );

  const payoutGroups = useMemo(
    () =>
      sortBy(
        filter(shopData?.groups, (group) => group.payout),
        (group) => group.payoutOrdersAccessTimeout,
      ),
    [shopData],
  );

  const initialValues: PayoutValues = useMemo(() => {
    if (shopData) {
      return {
        payoutTradeMethodsConfigs: shopData.payoutTradeMethodsConfigs || [],
        payoutOrderMinAmount: shopData.payoutOrderMinAmount || 0,
        payoutOrderMaxAmount: shopData.payoutOrderMaxAmount || 0,
        payoutMaxTimeout: shopData.payoutMaxTimeout ?? null,
        payoutCreateOrderResponseTimeout:
          shopData.payoutCreateOrderResponseTimeout ?? null,
      };
    }
    return {
      payoutTradeMethodsConfigs: [],
      payoutOrderMinAmount: 0,
      payoutOrderMaxAmount: 0,
      payoutMaxTimeout: null,
      payoutCreateOrderResponseTimeout: null,
    };
  }, [shopData]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        payoutTradeMethodsConfigs: Yup.array().of(
          Yup.object().shape({
            tradeMethodId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        ...validationUtils.getNaturalNumberRangeValidationSchema(
          'payoutOrderMinAmount',
          'payoutOrderMaxAmount',
        ),
        payoutMaxTimeout: Yup.number().optional().nullable(),
        payoutCreateOrderResponseTimeout: Yup.number().optional().nullable(),
      }),
    [tCommon],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: submitShop,
  });

  useUnsavedChangesGuard(formik);

  const isDataLoaded = useMemo(() => !!shopData?.name, [shopData?.name]);

  return (
    <FormikProvider value={formik}>
      <Form>
        {/* 1. Trade Methods */}
        {isAdminOrTechOperator && (
          <ManageShopAccordion
            title={t('sections.trade_methods.title')}
            defaultExpanded
          >
            <FormControls>
              <ShopPayoutTradeMethods
                key={isDataLoaded ? 'loaded' : 'empty'}
                fiatCurrencyId={shopData?.fiatCurrencyId || ''}
                hideTitle
              />
            </FormControls>
          </ManageShopAccordion>
        )}

        {/* 2. Order Routing (Groups) */}
        {isAdminOrTechOperator && !isNew && (
          <ManageShopAccordion title={t('sections.order_routing')}>
            <FormControls>
              <ShopPayoutGroups groups={payoutGroups} />
            </FormControls>
          </ManageShopAccordion>
        )}

        {/* 3. Amounts */}
        <ManageShopAccordion
          title={t('sections.payout_order_amount_limits')}
          defaultExpanded={!isAdminOrTechOperator}
        >
          <FormControls>
            <FormControls row>
              <FormikNumericField
                name="payoutOrderMinAmount"
                label={tCommon('common.min')}
                fullWidth
                disabled={!isAdminOrTechOperator}
                suffix={shopFiatCurrencyCode}
                allowNegative={false}
              />
              <FormikNumericField
                name="payoutOrderMaxAmount"
                label={tCommon('common.max')}
                fullWidth
                disabled={!isAdminOrTechOperator}
                suffix={shopFiatCurrencyCode}
                allowNegative={false}
              />
            </FormControls>
          </FormControls>
        </ManageShopAccordion>

        {/* 4. Timeouts */}
        <ManageShopAccordion title={t('sections.timeouts')}>
          <FormControls>
            <FormikNumericField
              name="payoutMaxTimeout"
              label={t('fields.payout_max_timeout')}
              allowNegative={false}
              decimalScale={0}
              disabled={!isAdminOrTechOperator}
            />
            <FormikNumericField
              name="payoutCreateOrderResponseTimeout"
              label={t('fields.create_order')}
              allowNegative={false}
              decimalScale={0}
              disabled={!isAdminOrTechOperator}
              defaultValue={0}
            />
          </FormControls>
        </ManageShopAccordion>

        <ManageShopTabActions />
      </Form>
    </FormikProvider>
  );
};
